// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import {
  getSharedBffWebSocket,
  TourCreatedEvent,
  TourInitiateRejectedEvent,
} from '@/services/bffWebSocket';
import { setCurrentTourId } from '@/services/currentTour';
import { ensureCurrentPlayer } from '@/services/ensureCurrentPlayer';
import { generateId } from '@/features/tour-edit/utils';
import { isValidTourName, normalizeTourName } from '@/services/tourName';

const CREATE_ACK_TIMEOUT_MS = 12000;

type Options = {
  /** Called with the new tour id once the server confirms creation. */
  onCreated: (tourId: string) => void;
};

/**
 * Creating a tour was duplicated across /welcome and both /my-tours screens.
 * It lives here so the three entry points cannot drift, and so creation stays
 * behind an explicit name — nothing is sent until createTour is called.
 */
export function useCreateTour({ onCreated }: Options) {
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendingRequestIdRef = useRef<string | null>(null);
  // Holds the payload when the socket is not open yet; flushed on reconnect.
  const pendingPayloadRef = useRef<Record<string, unknown> | null>(null);
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);

    const unsubscribeMessage = socket.onMessage((message) => {
      const messageType = (message as { type?: string }).type;

      if (messageType === 'tour.initiate.rejected') {
        const payload = message as TourInitiateRejectedEvent;
        if (!pendingRequestIdRef.current || payload.requestId !== pendingRequestIdRef.current) return;
        pendingRequestIdRef.current = null;
        pendingPayloadRef.current = null;
        setCreating(false);
        const translated = payload.messageKey ? t(payload.messageKey) : '';
        setError(
          translated && payload.messageKey && translated !== payload.messageKey
            ? translated
            : payload.message || t('tournaments.createFailed')
        );
        return;
      }

      if (messageType === 'tour.created') {
        const payload = message as TourCreatedEvent;
        const incomingRequestId = payload.requestId ?? payload.RequestId;
        if (!pendingRequestIdRef.current || incomingRequestId !== pendingRequestIdRef.current) return;
        pendingRequestIdRef.current = null;
        pendingPayloadRef.current = null;
        setCreating(false);
        const createdId = payload.tourId ?? payload.TourId ?? null;
        setCurrentTourId(createdId);
        if (createdId) onCreatedRef.current(createdId);
      }
    });

    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state !== 'connected') return;
      const payload = pendingPayloadRef.current;
      if (!payload) return;
      pendingPayloadRef.current = null;
      socket.send(payload);
    });

    return () => {
      unsubscribeMessage();
      unsubscribeConnection();
    };
  }, [t, user?.token]);

  // Watchdog: without an ack the modal would spin forever.
  useEffect(() => {
    if (!creating || !pendingRequestIdRef.current) return;
    const timer = setTimeout(() => {
      if (!pendingRequestIdRef.current) return;
      pendingRequestIdRef.current = null;
      pendingPayloadRef.current = null;
      setCreating(false);
      setError(t('tournaments.createFailed'));
    }, CREATE_ACK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [creating, t]);

  const createTour = useCallback(
    async (rawName: string) => {
      if (creating) return;

      const tourName = normalizeTourName(rawName);
      if (!isValidTourName(tourName)) {
        setError(t('tournaments.nameRequired'));
        return;
      }
      if (!user?.token || !user.id) {
        setError(t('tournaments.createFailed'));
        return;
      }

      setCreating(true);
      setError(null);

      const ensured = await ensureCurrentPlayer(apiBaseUrl, user.token, user.id, {
        timeoutMs: 10_000,
        intervalMs: 400,
        // A freshly registered user's player record arrives asynchronously; without this the
        // very first "Create tour" gives up on the first 404 and blames the user.
        retryWhileMissing: true,
      });
      if (ensured.status !== 'resolved') {
        setCreating(false);
        setError(
          ensured.status === 'unauthorized'
            ? t('tournaments.createFailed')
            : t('tournaments.creatorMemberRequired')
        );
        return;
      }

      const socket = socketRef.current;
      socket.setAuthToken(user.token);
      const requestId = generateId();
      pendingRequestIdRef.current = requestId;
      const payload = {
        type: 'tour.initiate',
        requestId,
        tourName,
        playerIds: [ensured.playerId],
      };
      // The ws client queues and flushes this frame itself when the socket (re)opens, so a
      // 'queued' outcome must NOT also be stashed here — the reconnect resend would create the
      // tour twice. Only a dropped frame (never for tour.initiate today) needs the manual stash.
      if (socket.send(payload) === 'dropped') {
        pendingPayloadRef.current = payload;
      }
    },
    [apiBaseUrl, creating, t, user?.id, user?.token]
  );

  const clearError = useCallback(() => setError(null), []);

  return { createTour, creating, error, clearError };
}
